import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useApp } from '../contexts/AppContext';
import { ChevronLeftIcon, BellRingIcon, CheckCircle2Icon } from 'lucide-react';
import { wardNames } from '../data/checklists';
interface StaffBuzzerProps {
  onBack: () => void;
}
export function StaffBuzzer({
  onBack
}: StaffBuzzerProps) {
  const {
    colors
  } = useTheme();
  const {
    allStaff
  } = useApp();
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedWard, setSelectedWard] = useState('');
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [isBuzzing, setIsBuzzing] = useState(false);
  const wards = Object.entries(wardNames).map(([id, name]) => ({
    id,
    name
  }));
  const handleBuzzStaff = async () => {
    if (!selectedStaff || !selectedWard) return;
    setShowModal(true);
    setIsBuzzing(true);
    // Simulate buzz sending
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsBuzzing(false);
    setShowModal(false);
    setShowSuccessToast(true);
    // Reset form
    setTimeout(() => {
      setShowSuccessToast(false);
      setSelectedStaff('');
      setSelectedWard('');
      setMessage('');
    }, 3000);
  };
  const selectedStaffName = allStaff.find(s => s.id === selectedStaff)?.name || '';
  const selectedWardName = wardNames[selectedWard] || '';
  const isFormValid = selectedStaff && selectedWard;
  return <div className="min-h-screen -m-4" style={{
    backgroundColor: colors.background
  }} data-id="element-530">
      {/* Header */}
      <div className="sticky top-0 z-10 p-4 border-b" style={{
      backgroundColor: colors.cardSurface,
      borderColor: colors.divider
    }} data-id="element-531">
        <div className="flex items-center gap-4" data-id="element-532">
          <button onClick={onBack} className="p-2 -ml-2 rounded-lg" style={{
          color: colors.textPrimary
        }} data-id="element-533">
            <ChevronLeftIcon size={24} data-id="element-534" />
          </button>
          <div data-id="element-535">
            <h1 className="text-xl font-bold" style={{
            color: colors.textPrimary
          }} data-id="element-536">
              Staff Buzzer 📳
            </h1>
            <p className="text-sm" style={{
            color: colors.textSecondary
          }} data-id="element-537">
              Alert staff members instantly
            </p>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-6" data-id="element-538">
        {/* Select Staff */}
        <div data-id="element-539">
          <label className="block text-sm font-semibold mb-2" style={{
          color: colors.textPrimary
        }} data-id="element-540">
            Select Staff
          </label>
          <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)} className="w-full p-4 rounded-lg border" style={{
          backgroundColor: colors.cardSurface,
          color: colors.textPrimary,
          borderColor: colors.divider
        }} data-id="element-541">
            <option value="" data-id="element-542">Choose a staff member...</option>
            {allStaff.map(staff => <option key={staff.id} value={staff.id} data-id="element-543">
                {staff.name} ({staff.role})
              </option>)}
          </select>
        </div>
        {/* Select Ward */}
        <div data-id="element-544">
          <label className="block text-sm font-semibold mb-2" style={{
          color: colors.textPrimary
        }} data-id="element-545">
            Select Ward to Buzz To
          </label>
          <select value={selectedWard} onChange={e => setSelectedWard(e.target.value)} className="w-full p-4 rounded-lg border" style={{
          backgroundColor: colors.cardSurface,
          color: colors.textPrimary,
          borderColor: colors.divider
        }} data-id="element-546">
            <option value="" data-id="element-547">Choose a ward...</option>
            {wards.map(ward => <option key={ward.id} value={ward.id} data-id="element-548">
                {ward.name}
              </option>)}
          </select>
        </div>
        {/* Optional Message */}
        <div data-id="element-549">
          <label className="block text-sm font-semibold mb-2" style={{
          color: colors.textPrimary
        }} data-id="element-550">
            Optional Message
          </label>
          <textarea value={message} onChange={e => setMessage(e.target.value.slice(0, 100))} placeholder="Add a message (optional, max 100 characters)" maxLength={100} rows={3} className="w-full p-4 rounded-lg border resize-none" style={{
          backgroundColor: colors.cardSurface,
          color: colors.textPrimary,
          borderColor: colors.divider
        }} data-id="element-551" />
          <div className="text-xs mt-1 text-right" style={{
          color: colors.textSecondary
        }} data-id="element-552">
            {message.length} / 100
          </div>
        </div>
        {/* Buzz Button */}
        <button onClick={handleBuzzStaff} disabled={!isFormValid} className="w-full p-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50" style={{
        backgroundColor: isFormValid ? colors.accent : colors.divider,
        color: '#FFFFFF'
      }} data-id="element-553">
          <BellRingIcon size={20} data-id="element-554" />
          Buzz Staff
        </button>
      </div>
      {/* Buzzing Modal */}
      {showModal && <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-50" data-id="element-555" />
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 rounded-2xl max-w-md mx-auto p-8" style={{
        backgroundColor: colors.cardSurface
      }} data-id="element-556">
            <div className="text-center" data-id="element-557">
              <div className="text-6xl mb-4 animate-bounce" data-id="element-558">📳</div>
              <h3 className="text-xl font-bold mb-2" style={{
            color: colors.textPrimary
          }} data-id="element-559">
                {isBuzzing ? 'Buzzing Staff...' : 'Buzz Sent!'}
              </h3>
              {!isBuzzing && <p style={{
            color: colors.textSecondary
          }} data-id="element-560">
                  Successfully buzzed {selectedStaffName}
                </p>}
            </div>
          </div>
        </>}
      {/* Success Toast */}
      {showSuccessToast && <div className="fixed bottom-24 left-1/2 transform -translate-x-1/2 px-6 py-4 rounded-xl shadow-lg z-50 flex items-center gap-3 max-w-sm" style={{
      backgroundColor: colors.success,
      color: '#FFFFFF'
    }} data-id="element-561">
          <CheckCircle2Icon size={24} data-id="element-562" />
          <div className="text-sm" data-id="element-563">
            <div className="font-semibold" data-id="element-564">
              Buzz sent to {selectedStaffName}
            </div>
            <div className="opacity-90" data-id="element-565">in {selectedWardName}</div>
          </div>
        </div>}
    </div>;
}